<template>
  <LoadingWrapper :state="status">
    <SideBarLayoutContent>
      <template #top-bar>
        <DashboardHeader />
      </template>
      <div class="flex flex-col gap-10">
        <SuperpowerTask
          v-if="data.superpowerTask && !dismissedSuperpowerTask"
          :superpowerTask="data.superpowerTask"
        />

        <template
          v-if="
            coachingInvitationsStatus == ApiRequestStatus.Success &&
            hasModule(ApplicationModule.COACHING)
          "
        >
          <div v-if="coachingInvitations.length">
            <h2 class="text-title-medium mb-2">Einladungen</h2>
            <div
              v-for="(coachingInvitation, idx) in coachingInvitations"
              :key="idx"
              class="flex flex-col gap-2"
            >
              <CoachingInvitationItem
                :coach="coachingInvitation.coach"
                :created-at="coachingInvitation.createdAt"
                @accept-coaching-invitation="
                  (coachId) => updateCoachingInvitation(coachId, user.id, CoachingStatus.Active)
                "
                @reject-coaching-invitation="
                  (coachId) => updateCoachingInvitation(coachId, user.id, CoachingStatus.Rejected)
                "
              />
            </div>
          </div>
        </template>
        <div
          v-if="
            loadFeedbackTasksStatus == ApiRequestStatus.Success &&
            loadFeedbackGoalsStatus == ApiRequestStatus.Success
          "
          class="flex flex-col"
        >
          <div class="mb-1.5 flex w-full flex-row items-center">
            <h2 class="text-title-medium grow">To-Dos</h2>
            <BaseTextButton size="XS" color="text" @click="navigateToTodos">{{
              $t('common.all')
            }}</BaseTextButton>
          </div>
          <div
            v-if="numberOfTodos > 0 || recurringTasks.length > 0 || feedbackGoals.length > 0"
            class="flex flex-col"
          >
            <div class="flex flex-col gap-4">
              <ul v-if="numberOfTodos > 0" class="flex w-full flex-col gap-0.5">
                <li
                  v-for="todo in todos"
                  :key="todo.id"
                  class="first:[&>div]:rounded-t-large! last:[&>div]:rounded-b-large! [&>div]:rounded-small! w-full"
                >
                  <FeedbackTask :task="todo" />
                </li>
              </ul>
            </div>
            <div v-if="feedbackGoals.length > 0" class="mt-4">
              <BaseHorizontalOverflow show-buttons class="relative w-full">
                <ul class="flex w-fit flex-row justify-stretch gap-4">
                  <li v-for="feedbackGoal in feedbackGoals" :key="feedbackGoal.id" class="min-w-sm">
                    <FeedbackGoal :feedbackGoal="feedbackGoal" class="w-full" />
                  </li>
                </ul>
              </BaseHorizontalOverflow>
            </div>
          </div>
          <div v-else>
            <EmptyStateCompact
              :title="$t('todos.empty.title')"
              :description="$t('todos.empty.description')"
              icon="check"
            />
          </div>
        </div>
        <div v-if="loadSurveyStatus == ApiRequestStatus.Success && surveys.length > 0">
          <div class="mb-1.5 flex flex-row items-center">
            <h2 class="text-title-medium grow">Laufende Umfragen</h2>
            <BaseTextButton size="XS" color="text" @click="navigateToSurveys">{{
              $t('common.all')
            }}</BaseTextButton>
          </div>
          <BaseHorizontalOverflow :show-buttons="true" class="relative w-full">
            <div class="flex w-fit flex-row justify-stretch gap-4">
              <SurveyCard
                v-for="survey in surveys"
                :key="survey.templateId"
                :survey="survey"
                class="w-sm"
              />
            </div>
          </BaseHorizontalOverflow>
        </div>
        <template v-if="data.currentObjectives.length">
          <div>
            <div class="mb-1.5 flex flex-row items-center">
              <h2 class="text-title-medium grow">Ziele</h2>
              <BaseTextButton size="XS" color="text" @click="navigateToObjectives">{{
                $t('common.all')
              }}</BaseTextButton>
            </div>
            <BaseHorizontalOverflow show-buttons>
              <ul class="flex w-fit flex-row justify-stretch gap-4">
                <li
                  v-for="objective in data.currentObjectives"
                  :key="objective.id"
                  class="bg-surface-container rounded-large flex w-sm flex-col justify-center px-4 py-3"
                >
                  <h4 class="text-title-medium line-clamp-2 text-ellipsis">
                    {{ objective.title }}
                  </h4>
                  <span
                    v-if="objective.description"
                    class="text-body-small text-on-surface-variant line-clamp-1 text-ellipsis"
                    >{{ objective.description }}</span
                  >
                </li>
              </ul>
            </BaseHorizontalOverflow>
          </div>
        </template>

        <div
          v-if="
            loadFeedbacksStatus == ApiRequestStatus.Success &&
            user.memberPermissions.includes(Permission.FEEDBACK_RECEIVE)
          "
          class="flex flex-col"
        >
          <div class="mb-1.5 flex flex-row items-center">
            <h2 class="text-title-medium grow">Neuste Feedbacks</h2>
            <BaseTextButton size="XS" color="text" @click="navigateToFeedbacks">{{
              $t('common.all')
            }}</BaseTextButton>
          </div>
          <ul v-if="feedbacks.length > 0" class="flex flex-col gap-0.5">
            <li
              v-for="feedback in feedbacks"
              :key="feedback.id"
              class="bg-surface-container rounded-extra-small first:rounded-t-large last:rounded-b-large overflow-hidden"
            >
              <FeedbackSurveyHistoryItem
                v-if="feedback.type == 'structured'"
                :feedback="feedback"
              />
              <FeedbackSpontaneousHistoryItem v-else :feedback="feedback" />
            </li>
          </ul>
          <div v-else>
            <EmptyStateCompact :title="$t('feedbackHistory.empty.description')" icon="forum" />
          </div>
        </div>
      </div>
    </SideBarLayoutContent>
  </LoadingWrapper>
</template>

<script setup lang="ts">
  import LoadingWrapper from '@/core/components/loading/LoadingWrapper.vue'
  import { useDashboard } from '../composables/useDashboard'
  import DashboardHeader from '../components/DashboardHeader.vue'
  import SideBarLayoutContent from '../../../core/components/layouts/SideBarLayoutContent.vue'
  import FeedbackSurveyHistoryItem from '../../view-feedback/components/feedback-history-item/FeedbackSurveyHistoryItem.vue'
  import FeedbackSpontaneousHistoryItem from '../../view-feedback/components/feedback-history-item/FeedbackSpontaneousHistoryItem.vue'
  import BaseTextButton from '../../../core/components/base/button/text-button/BaseTextButton.vue'
  import { useRouter } from 'vue-router'
  import BaseHorizontalOverflow from '../../../core/components/base/horizontal-overflow/BaseHorizontalOverflow.vue'
  import SurveyCard from '../components/SurveyCard.vue'
  import { ApiRequestStatus } from '../../../core/models/api/api-request-status'
  import CoachingInvitationItem from '../components/CoachingInvitationItem.vue'
  import { CoachingStatus, useCoachingInvitations } from '../composables/useCoachingInvitations'
  import { useUserStore } from '@/stores/user.store'
  import { Permission } from '../../../core/models/user'
  import { ApplicationModule } from '@/core/models/organization'
  import { useApplicationModule } from '@/core/composables/use-application-module'
  import { useLocalStorage } from '@vueuse/core'
  import SuperpowerTask from '../components/SuperpowerTask.vue'
  import FeedbackTask from '@/core/components/feedback-task/FeedbackTask.vue'
  import FeedbackGoal from '@/core/components/feedback-task/FeedbackGoal.vue'
  import EmptyStateCompact from '@/core/components/empty-states/EmptyStateCompact.vue'

  const { user } = useUserStore()

  const {
    data,
    surveys,
    status,
    feedbacks,
    loadFeedbacksStatus,
    loadSurveyStatus,
    todos,
    numberOfTodos,
    loadFeedbackTasksStatus,
    recurringTasks,
    feedbackGoals,
    loadFeedbackGoalsStatus,
  } = useDashboard()

  const { hasModule } = useApplicationModule()

  const { coachingInvitations, coachingInvitationsStatus, updateCoachingInvitation } =
    useCoachingInvitations()

  const router = useRouter()

  const dismissedSuperpowerTask = useLocalStorage('dismissed-superpower-task', false)

  const navigateToSurveys = (): void => {
    router.push({ name: 'surveys' })
  }
  const navigateToFeedbacks = (): void => {
    router.push({ name: 'feedbacks' })
  }
  const navigateToTodos = (): void => {
    router.push({ name: 'todos' })
  }
  const navigateToObjectives = (): void => {
    router.push({ name: 'objectives' })
  }
</script>
